"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridTableApiImpl = void 0;
exports.useGridTableApi = useGridTableApi;
exports.maybeApply = maybeApply;
const mobx_1 = require("mobx");
const mobx_utils_1 = require("mobx-utils");
const react_1 = require("react");
const index_1 = require("../index");
const TableState_1 = require("./utils/TableState");
const utils_1 = require("../../utils");
/**
 * Creates an `api` handle to drive a `GridTable`.
 *
 * ```
 * const api = useGridTableApi<Row>();
 * const count = useComputed(() => api.getSelectedRows().length, [api]);
 * ...
 * return <GridTable api={api} />
 * ```
 *
 * This is very similar to a `useRef`, except that the parent function has
 * immediate access to `api` and can use it for `useComputed`, instead of
 * having to wait for `ref.current` to be set after the child `GridTable`
 * has run.
 */
function useGridTableApi() {
    return (0, react_1.useMemo)(() => new GridTableApiImpl(), []);
}
// Using `FooImpl`to keep the public GridTableApi definition separate.
class GridTableApiImpl {
    constructor() {
        // This is public to GridTable but not exported outside of Beam
        this.tableState = new TableState_1.TableState(this);
        this.virtuosoRef = { current: null };
        // This instance gets spread into each row's GridRowApi, so bind the methods up-front
        bindMethods(this);
        // Memoize these so that if the user is creating new `data` instances on every render, they
        // can use `getSelectedRowIds` to observer a stable list of `[pi:1, pi:2]`, etc.
        this.getVisibleRowsImpl = (0, mobx_utils_1.computedFn)(this.getVisibleRowsImpl, { equals: mobx_1.comparer.shallow });
        this.getVisibleRowIdsImpl = (0, mobx_utils_1.computedFn)(this.getVisibleRowIdsImpl, { equals: mobx_1.comparer.shallow });
        this.getSelectedRowsImpl = (0, mobx_utils_1.computedFn)(this.getSelectedRowsImpl, { equals: mobx_1.comparer.shallow });
        this.getSelectedRowIdsImpl = (0, mobx_utils_1.computedFn)(this.getSelectedRowIdsImpl, { equals: mobx_1.comparer.shallow });
    }
    /** Called once by the GridTable when it takes ownership of this api instance. */
    init(persistCollapse, virtuosoRef) {
        // Technically this drives both row-collapse and column-expanded
        if (persistCollapse)
            this.tableState.loadCollapse(persistCollapse);
        this.virtuosoRef = virtuosoRef;
        this.lookup = (0, index_1.createRowLookup)(this, virtuosoRef);
    }
    scrollToIndex(index) {
        this.virtuosoRef.current &&
            this.virtuosoRef.current.scrollToIndex(typeof index === "number" ? { index, behavior: "smooth" } : index);
    }
    getSelectedRowIds(kind) {
        return this.getSelectedRowIdsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getSelectedRowIdsImpl(kind) {
        return this.tableState.selectedRows.filter((rs) => !kind || rs.kind === kind).map((rs) => rs.row.id);
    }
    getSelectedRows(kind) {
        return this.getSelectedRowsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getSelectedRowsImpl(kind) {
        return this.tableState.selectedRows.filter((rs) => !kind || rs.kind === kind).map((rs) => rs.row);
    }
    // The `any` is not great, but getting the overload to handle the optional kind is annoying
    getVisibleRows(kind) {
        return this.getVisibleRowsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getVisibleRowsImpl(kind) {
        return this.tableState.visibleRows.filter((row) => !kind || row.kind === kind).map((rs) => rs.row);
    }
    getVisibleRowIds(kind) {
        return this.getVisibleRowIdsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getVisibleRowIdsImpl(kind) {
        return this.tableState.visibleRows.filter((row) => !kind || row.kind === kind).map((rs) => rs.row.id);
    }
    clearSelections(id) {
        this.tableState.selectRow("header", false);
    }
    setActiveRowId(id) {
        this.tableState.activeRowId = id;
    }
    setActiveCellId(id) {
        this.tableState.activeCellId = id;
    }
    selectRow(id, selected = true) {
        this.tableState.selectRow(id, selected);
    }
    toggleCollapsedRow(id) {
        this.tableState.toggleCollapsed(id);
    }
    isCollapsedRow(id) {
        return this.tableState.isCollapsed(id);
    }
    setVisibleColumns(ids) {
        this.tableState.setVisibleColumns(ids);
    }
    getVisibleColumnIds() {
        return this.tableState.visibleColumnIds;
    }
    deleteRows(ids) {
        this.tableState.deleteRows(ids);
    }
    downloadToCsv(fileName) {
        // Create a link element, set the download attribute with the provided filename
        const link = document.createElement("a");
        if (link.download === undefined)
            throw new Error("This browser does not support the download attribute.");
        // Create a Blob from the CSV content
        const url = URL.createObjectURL(new Blob([this.generateCsvContent().join("\n")], { type: "text/csv;charset=utf-8;" }));
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    copyToClipboard() {
        // Copy the CSV content to the clipboard
        const content = this.generateCsvContent().join("\n");
        return navigator.clipboard.writeText(content).catch((err) => {
            // Let the user know the copy failed...
            window.alert("Failed to copy to clipboard, probably due to browser restrictions.");
            throw err;
        });
    }
    // visibleForTesting, not part of the GridTableApi
    // ...although maybe it could be public someday, to allow getting the raw the CSV content
    // and then sending it somewhere else, like directly to a gsheet.
    generateCsvContent() {
        var _a, _b;
        const csvPrefixRows = (_b = (_a = this.tableState.csvPrefixRows) === null || _a === void 0 ? void 0 : _a.map((row) => row.map(escapeCsvValue).join(","))) !== null && _b !== void 0 ? _b : [];
        // Convert the array of rows into CSV format
        const dataRows = this.tableState.visibleRows.map((rs) => {
            const values = this.tableState.visibleColumns
                .filter((c) => !c.isAction)
                .map((c) => {
                // Just guessing for level=1
                const maybeContent = (0, index_1.applyRowFn)(c, rs.row, this, 1, true, undefined);
                if ((0, index_1.isGridCellContent)(maybeContent)) {
                    const cell = maybeContent;
                    const content = maybeApply(cell.content);
                    // Anything not isJSX (like a string) we can put into the CSV directly
                    if (!(0, index_1.isJSX)(content))
                        return content;
                    // Otherwise use the value/sortValue values
                    return (0, utils_1.isDefined)(cell.value) ? maybeApply(cell.value) : cell.sortValue ? maybeApply(cell.sortValue) : "-"; // Do we need the "-" handling unclear if we use it ever
                }
                else {
                    // ReactNode
                    return (0, index_1.isJSX)(maybeContent) ? "-" : maybeContent;
                }
            });
            return values.map(toCsvString).map(escapeCsvValue).join(",");
        });
        return [...csvPrefixRows, ...dataRows];
    }
}
exports.GridTableApiImpl = GridTableApiImpl;
function toCsvString(value) {
    if (value === null || value === undefined)
        return "";
    if (typeof value === "string")
        return value;
    if (typeof value === "number")
        return value.toString();
    if (typeof value === "boolean")
        return value ? "true" : "false";
    return String(value);
}
function escapeCsvValue(value) {
    // Wrap values with special chars in quotes, and double quotes themselves
    if (value.includes('"') || value.includes(",") || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}
function bindMethods(instance) {
    Object.getOwnPropertyNames(Object.getPrototypeOf(instance)).forEach((key) => {
        if (instance[key] instanceof Function && key !== "constructor")
            instance[key] = instance[key].bind(instance);
    });
}
function maybeApply(maybeFn) {
    return typeof maybeFn === "function" ? maybeFn() : maybeFn;
}
